/**
 * Post-login redirect is stored when an unauthenticated user hits a protected route
 * (`captureRedirectDestinationForLogin`, used from auth-guard). It is read on `/` after OAuth (main PostLoginRedirect).
 *
 * On intentional logout we clear the stored redirect and set a short-lived suppress flag
 * so AuthGuard does not re-save the current URL when auth drops before navigation.
 * The suppress flag is cleared when the user reaches `/login` or `/` post-login — OAuth
 * often skips `/login`, so both places are needed.
 */
import { ROUTES } from "@/constants/routes";

/** Key for storing the redirect destination after login */
enum SessionStorageKeys {
	REDIRECT_AFTER_LOGIN = "redirectAfterLogin",
	SUPPRESS_REDIRECT_CAPTURE = "suppressRedirectAfterLoginCapture",
}
/**
 * When an unauthenticated user hits a protected route, store the destination path in sessionStorage
 * unless the suppress flag is set (intentional logout via `prepareSessionStorageForLogout`) or the
 * destination is `/` or `/login`.
 */
export function captureRedirectDestinationForLogin(
	pathname: string,
	search: string,
	hash: string,
): void {
	const destination = pathname + search + hash;
	// If the suppress flag is set (intentional logout via `prepareSessionStorageForLogout`) or the destination is `/` or `/login`, do not store the redirect destination in sessionStorage.
	if (
		sessionStorage.getItem(SessionStorageKeys.SUPPRESS_REDIRECT_CAPTURE) ||
		destination === "/" ||
		destination === ROUTES.LOGIN
	) {
		return;
	}
	sessionStorage.setItem(SessionStorageKeys.REDIRECT_AFTER_LOGIN, destination);
}

/** After `/login` mount or successful post-login navigation to `/`.
 *  - Clear the stored redirect so a later unauthenticated visit can store a redirect.
 */
export function clearSuppressRedirectCapture(): void {
	sessionStorage.removeItem(SessionStorageKeys.SUPPRESS_REDIRECT_CAPTURE);
}

/** Read-once: return stored post-login path (default `/dashboard`), remove it, and clear suppress capture.
 * Used on `/` after OAuth (see main.tsx PostLoginRedirect).
 */
export function consumePostLoginRedirectPath(): string {
	const path =
		sessionStorage.getItem(SessionStorageKeys.REDIRECT_AFTER_LOGIN) ??
		ROUTES.DASHBOARD;
	sessionStorage.removeItem(SessionStorageKeys.REDIRECT_AFTER_LOGIN);
	clearSuppressRedirectCapture();
	return path;
}

/** Call before `kindeLogout()` from the app logout action
 *  - Clear the stored redirect so a later unauthenticated visit can store a redirect.
 *  - Set a short-lived suppress flag so AuthGuard does not re-save the current URL when auth drops before navigation.
 *    The suppress flag is cleared when the user reaches `/login` or `/` post-login — OAuth
 *    often skips `/login`, so both places are needed.
 */
export function prepareSessionStorageForLogout(): void {
	// Clear the stored redirect so a later unauthenticated visit can store a redirect.
	sessionStorage.removeItem(SessionStorageKeys.REDIRECT_AFTER_LOGIN);
	// Set a short-lived suppress flag so AuthGuard does not re-save the current URL via captureRedirectDestinationForLogin() when the user intentionally logs out of the app.
	sessionStorage.setItem(SessionStorageKeys.SUPPRESS_REDIRECT_CAPTURE, "1");
}
